import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { BusinessDashboardRecord } from "@/hooks/admin/BusinessDashboardTab/useBusinessDashboardMetrics";
import { formatCurrency } from "@/utils/shared/currencyUtils";
import { formatFullNumericDateUTC } from "@/utils/shared/dateUtils";
import { compareDesc, parseISO } from "date-fns";

interface BusinessDashboardDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  month: string;
  incomeRecords: BusinessDashboardRecord[];
  expensesRecords: BusinessDashboardRecord[];
}

const BusinessDashboardDetailModal = ({
  isOpen,
  onClose,
  month,
  incomeRecords,
  expensesRecords,
}: BusinessDashboardDetailModalProps) => {
  const sortedIncomeRecords = [...incomeRecords].sort((a, b) => 
    compareDesc(parseISO(a.payment_date), parseISO(b.payment_date))
  );
  
  const sortedExpensesRecords = [...expensesRecords].sort((a, b) => 
    compareDesc(parseISO(a.payment_date), parseISO(b.payment_date))
  );
  
  const totalIncome = incomeRecords.reduce((sum, record) => sum + record.total_payment, 0);
  const totalExpenses = expensesRecords.reduce((sum, record) => sum + record.total_payment, 0);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            Detalle Financiero - {month}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-6">
          {/* Ingresos Section */}
          <div>
            <h3 className="text-lg font-semibold mb-3 text-success">Ingresos</h3>
            {incomeRecords.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground text-sm">
                No hay ingresos para este mes
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Propiedad</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedIncomeRecords.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell>
                        {formatFullNumericDateUTC(record.payment_date)}
                      </TableCell>
                      <TableCell>
                        {record.property_short_name || "-"}
                      </TableCell>
                      <TableCell>
                        {record.description || "-"}
                      </TableCell>
                      <TableCell className="text-right font-medium text-success">
                        {formatCurrency(record.total_payment)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Egresos Section */}
          <div>
            <h3 className="text-lg font-semibold mb-3 text-muted-foreground">Egresos</h3>
            {expensesRecords.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground text-sm">
                No hay egresos para este mes
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Propiedad</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedExpensesRecords.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell>
                        {formatFullNumericDateUTC(record.payment_date)}
                      </TableCell>
                      <TableCell>
                        {record.property_short_name || "-"}
                      </TableCell>
                      <TableCell>
                        {record.expense_description || "-"}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(record.total_payment)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>

        {/* Totals Footer */}
        <div className="border-t pt-4 mt-4 space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">Total Ingresos:</span>
            <span className="text-lg font-bold text-success">
              {formatCurrency(totalIncome)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">Total Egresos:</span>
            <span className="text-lg font-bold text-muted-foreground">
              {formatCurrency(totalExpenses)}
            </span>
          </div>
          <div className="flex justify-between items-center pt-2 border-t">
            <span className="text-lg font-semibold">Utilidad Neta:</span>
            <span className={`text-xl font-bold ${
              (totalIncome - totalExpenses) >= 0 ? "text-success" : "text-destructive"
            }`}>
              {formatCurrency(totalIncome - totalExpenses)}
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BusinessDashboardDetailModal;